import React, { useState, useEffect, useRef } from 'react';
import { getReportForEdit, updateReportByManager, createReport } from '../api';
import './EditReportModal.css';

const EditReportModal = ({ reportId, factory, master, initialReportDate, onClose, onSave }) => {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const textAreasRefs = useRef([]);
    const pointerDownStartedInModalRef = useRef(false);

    useEffect(() => {
        if (reportId) {
            loadReport();
        } else {
            setReport({
                reportDate: initialReportDate,
                factory: factory,
                master: master,
                atStationToday: 0,
                totalInCity: 0,
                newWorkersToday: 0,
                onMedicalToday: 0,
                leftFactoryToday: 0,
                onVacationToday: 0,
                requestToday: 0,
                whoReturnedFromVacation: '',
                whoLeftForVacation: '',
                whoOnSickLeave: '',
                whoAtFactory: '',
                whoInCity: '',
                whoFiredToday: '',
                whoOnApproval: '',
                whoOtrabotkaToday: '',
                requestTodayComment: ''
            });
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportId, initialReportDate, factory, master]);

    const loadReport = async () => {
        try {
            setLoading(true);
            const response = await getReportForEdit(reportId);
            setReport(response.data);
        } catch (err) {
            setError('Ошибка при загрузке отчета');
            console.error('Error loading report:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!report) return;

        try {
            setSaving(true);
            setError(null);

            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            let reportData;
            if (reportId) {
                reportData = {
                    ...report,
                    timeZone: userTimeZone
                };
            } else {
                reportData = {
                    reportDate: report.reportDate,
                    factory: {
                        id: factory.id
                    },
                    atStationToday: report.atStationToday,
                    totalInCity: report.totalInCity,
                    newWorkersToday: report.newWorkersToday,
                    onMedicalToday: report.onMedicalToday,
                    leftFactoryToday: report.leftFactoryToday,
                    onVacationToday: report.onVacationToday,
                    requestToday: report.requestToday,
                    whoReturnedFromVacation: report.whoReturnedFromVacation,
                    whoLeftForVacation: report.whoLeftForVacation,
                    whoOnSickLeave: report.whoOnSickLeave,
                    whoAtFactory: report.whoAtFactory,
                    whoInCity: report.whoInCity,
                    whoFiredToday: report.whoFiredToday,
                    whoOnApproval: report.whoOnApproval,
                    whoOtrabotkaToday: report.whoOtrabotkaToday,
                    requestTodayComment: report.requestTodayComment,
                    timeZone: userTimeZone
                };
            }

            let savedReport;
            if (reportId) {
                await updateReportByManager(reportId, reportData);
                savedReport = reportData;
            } else {
                const response = await createReport(reportData);
                savedReport = response.data;
            }

            if (onSave) {
                onSave(savedReport);
            }
            onClose();
        } catch (err) {
            const serverErrorData = err?.response?.data;
            const serverErrorText =
                (typeof serverErrorData === 'string' ? serverErrorData : null) ||
                serverErrorData?.detail ||
                serverErrorData?.message ||
                err?.message;

            setError(serverErrorText || 'Ошибка при сохранении отчета');
            console.error('Error saving report:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleTextareaChange = (e, field) => {
        const text = e.target.value;
        setReport({ ...report, [field]: text });
        autoResizeTextarea(e.target);
    };

    const autoResizeTextarea = (textarea) => {
        textarea.style.height = 'auto';
        textarea.style.height = `${Math.max(textarea.scrollHeight, 3 * 24)}px`;
    };

    const handleFocus = (e) => {
        e.target.select();
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && !pointerDownStartedInModalRef.current) {
            onClose();
        }
        pointerDownStartedInModalRef.current = false;
    };

    const handleModalPointerDown = () => {
        pointerDownStartedInModalRef.current = true;
    };

    const handleBackdropPointerDown = (e) => {
        if (e.target === e.currentTarget) {
            pointerDownStartedInModalRef.current = false;
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            onClose();
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return (
            <div className="modal-backdrop" onPointerDown={handleBackdropPointerDown} onClick={handleBackdropClick}>
                <div className="edit-report-modal">
                    <div className="modal-loading">Загрузка отчета...</div>
                </div>
            </div>
        );
    }

    if (!report) {
        return null;
    }

    const reportDate = new Date(report.reportDate);
    const reportDateTitle = reportDate.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
    const reportDateWeekday = reportDate.toLocaleDateString('ru-RU', { weekday: 'long' });

    return (
        <div className="modal-backdrop" onPointerDown={handleBackdropPointerDown} onClick={handleBackdropClick}>
            <div className="edit-report-modal" onPointerDown={handleModalPointerDown}>
                <div className="modal-header">
                    <h2>{reportId ? 'Редактирование отчета' : 'Создание отчета'}</h2>
                    <button className="modal-close-btn" onClick={onClose}>×</button>
                </div>

                <div className="modal-body">
                    <div className="report-info">
                        <h3>{factory?.name}</h3>
                        <p>{reportDateTitle} ({reportDateWeekday})</p>
                        {master && <p>Мастер: {master.fio}</p>}
                    </div>

                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="report-form">
                        <div className="form-group">
                            <label>Количество работников ВЧЕРА на смене (те, кто на выходном, тут не учитываются)</label>
                            <input
                                type="number"
                                min="0"
                                value={report.atStationToday || 0}
                                onFocus={handleFocus}
                                onChange={e => setReport({ ...report, atStationToday: parseInt(e.target.value) || 0 })}
                            />
                        </div>

                        <div className="form-group">
                            <label>Количество работников в городе ЗА ВЧЕРА? (те кто на смене + бл,адм, прогул, КРОМЕ МВО)</label>
                            <input
                                type="number"
                                min="0"
                                value={report.totalInCity || 0}
                                onFocus={handleFocus}
                                onChange={e => setReport({ ...report, totalInCity: parseInt(e.target.value) || 0 })}
                            />
                        </div>

                        <div className="form-group">
                            <label>Сколько работников в межвахтовом отпуске (общее кол-во чел на МВО) ЗА ВЧЕРА</label>
                            <input
                                type="number"
                                min="0"
                                value={report.onVacationToday || 0}
                                onFocus={handleFocus}
                                onChange={e => setReport({ ...report, onVacationToday: parseInt(e.target.value) || 0 })}
                            />
                        </div>

                        <div className="form-group">
                            <label>Кто вернулся с межвахты ВЧЕРА? (Фамилия И.О.)</label>
                            <textarea
                                rows="2"
                                value={report.whoReturnedFromVacation || ''}
                                ref={el => textAreasRefs.current[4] = el}
                                onChange={e => handleTextareaChange(e, 'whoReturnedFromVacation')}
                            />
                        </div>

                        <div className="form-group">
                            <label>Кто убыл на межвахту Фамилия И.О. и дата возврата ЗА ВЧЕРА?</label>
                            <textarea
                                rows="2"
                                value={report.whoLeftForVacation || ''}
                                ref={el => textAreasRefs.current[5] = el}
                                onChange={e => handleTextareaChange(e, 'whoLeftForVacation')}
                            />
                        </div>

                        <div className="form-group">
                            <label>Кто на больничном/выходном/отгуле/прогуле/административном? (ФИО-причина кратко) ЗА ВЧЕРА</label>
                            <textarea
                                rows="2"
                                value={report.whoOnSickLeave || ''}
                                ref={el => textAreasRefs.current[3] = el}
                                onChange={e => handleTextareaChange(e, 'whoOnSickLeave')}
                            />
                        </div>

                        <div className="form-group">
                            <label>Сколько СЕГОДНЯ новых работников вышло на смену</label>
                            <input
                                type="number"
                                min="0"
                                value={report.newWorkersToday || 0}
                                onFocus={handleFocus}
                                onChange={e => setReport({ ...report, newWorkersToday: parseInt(e.target.value) || 0 })}
                            />
                        </div>

                        <div className="form-group">
                            <label>Кто Новенькие вышли СЕГОДНЯ на смену (Фамилия И.О.)</label>
                            <textarea
                                rows="2"
                                value={report.whoAtFactory || ''}
                                ref={el => textAreasRefs.current[2] = el}
                                onChange={e => handleTextareaChange(e, 'whoAtFactory')}
                            />
                        </div>

                        <div className="form-group">
                            <label>Сколько СЕГОДНЯ кандидатов на оформлении/заселилось/медосмотре/тест/экзамен/ТБ/собеседование?</label>
                            <input
                                type="number"
                                min="0"
                                value={report.onMedicalToday || 0}
                                onFocus={handleFocus}
                                onChange={e => setReport({ ...report, onMedicalToday: parseInt(e.target.value) || 0 })}
                            />
                        </div>

                        <div className="form-group">
                            <label>Кто СЕГОДНЯ на оформлении/заселилось/медосмотре/тест/экзамен/ТБ/собеседование (Фамилия И.О.)</label>
                            <textarea
                                rows="2"
                                value={report.whoInCity || ''}
                                ref={el => textAreasRefs.current[1] = el}
                                onChange={e => handleTextareaChange(e, 'whoInCity')}
                            />
                        </div>

                        <div className="form-group">
                            <label>Кол-во работников уволенных ЗА ВЧЕРА</label>
                            <input
                                type="number"
                                value={report.leftFactoryToday || 0}
                                onFocus={handleFocus}
                                onChange={e => setReport({ ...report, leftFactoryToday: -Math.abs(parseInt(e.target.value) || 0) })}
                            />
                        </div>

                        <div className="form-group">
                            <label>Кто уволен ЗА ВЧЕРА? Фамилия И.О. и причина</label>
                            <textarea
                                rows="2"
                                value={report.whoFiredToday || ''}
                                ref={el => textAreasRefs.current[6] = el}
                                onChange={e => handleTextareaChange(e, 'whoFiredToday')}
                            />
                        </div>

                        <div className="form-group">
                            <label>Кто в работе: приезд (фамилия и дата), СБ, ждет отработки, ждём док-ты</label>
                            <textarea
                                rows="2"
                                value={report.whoOnApproval || ''}
                                ref={el => textAreasRefs.current[0] = el}
                                onChange={e => handleTextareaChange(e, 'whoOnApproval')}
                            />
                        </div>

                        <div className="form-group">
                            <label>Кто на отработке? Фамилия И.О. и планируемая дата увольнения</label>
                            <textarea
                                rows="2"
                                value={report.whoOtrabotkaToday || ''}
                                ref={el => textAreasRefs.current[7] = el}
                                onChange={e => handleTextareaChange(e, 'whoOtrabotkaToday')}
                            />
                        </div>

                        <div className="form-group">
                            <label>Количество человек по заявке на СЕГОДНЯ (общая цифра)</label>
                            <input
                                type="number"
                                min="0"
                                value={report.requestToday || 0}
                                onFocus={handleFocus}
                                onChange={e => setReport({ ...report, requestToday: parseInt(e.target.value) || 0 })}
                            />
                        </div>

                        <div className="form-group">
                            <label>Специальности по заявке НА СЕГОДНЯ (ПРИМЕР: 5 токарь, 3 фрезеровщик)</label>
                            <textarea
                                rows="2"
                                value={report.requestTodayComment || ''}
                                ref={el => textAreasRefs.current[8] = el}
                                onChange={e => handleTextareaChange(e, 'requestTodayComment')}
                            />
                        </div>
                    </form>
                </div>

                <div className="modal-footer">
                    <button
                        type="button"
                        className="cancel-btn"
                        onClick={onClose}
                        disabled={saving}
                    >
                        Отмена
                    </button>
                    <button
                        type="submit"
                        className="save-btn"
                        onClick={handleSubmit}
                        disabled={saving}
                    >
                        {saving ? 'Сохранение...' : 'Сохранить'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditReportModal;